import { createFileRoute } from '@tanstack/react-router'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Copy, Pencil, Plus, Trash2, Upload, X } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { Api } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Select from '@/components/ui/select'
import Tooltip from '@/components/ui/tooltip'
import { DatePicker } from '@/components/ui/datepicker'
import { SlotDialog, SlotRow } from '@/components/features/slots/slot-dialog'
import { EditSlotDialog } from '@/components/features/slots/edit-slot-dialog'
import { DeleteSlotDialog } from '@/components/features/slots/delete-slot-dialog'
import { UploadSlotsDialog } from '@/components/features/slots/upload-slots-dialog'
import { OutletDetails } from '@/lib/interfaces'
import dayjs from 'dayjs'

// Radix rejects an empty string as a select item value, so "no filter" needs a sentinel
// that can't collide with a real status or store id.
const ANY = '__any__'

// Slot lifecycle: Pending (not yet with Prosquad) -> Processing (waiting on riders) -> Assigned ->
// In-Progress -> Completed, or Cancelled when the window closed with no rider on it.
const statusStyles: Record<string, string> = {
    Pending: 'bg-yellow-100 text-yellow-700',
    Processing: 'bg-blue-100 text-blue-700',
    Assigned: 'bg-indigo-100 text-indigo-700',
    'In-Progress': 'bg-purple-100 text-purple-700',
    Completed: 'bg-green-100 text-green-700',
    Cancelled: 'bg-red-100 text-red-700',
}

// Riders are already out delivering (In-Progress) or the window has closed (Completed/Cancelled -
// settle_finished_slots moves both out of the live states) - Prosquad rejects an edit/close on any
// of these with a 409 REQUIREMENT_CLOSED, so there's nothing a webui edit/delete can still do here.
const LOCKED_STATUSES = new Set(['In-Progress', 'Completed', 'Cancelled'])

export const Route = createFileRoute('/_auth/slots')({
    component: RouteComponent,
})

function RouteComponent() {
    const { token, setPage } = useStore(useShallow(state => ({
        token: state.token,
        setPage: state.setPage,
    })))

    useEffect(() => { setPage('Slots') }, [])

    const [dateFilter, setDateFilter] = useState<Date | undefined>(() => new Date())
    // The backend now actually scopes /slot/get_all by this (format: YYYY-MM-DD) — without it,
    // an account with a large slot history (e.g. after a big bulk upload) pulls every slot ever
    // created on every load, which got very slow.
    const dateParam = dateFilter ? dayjs(dateFilter).format('YYYY-MM-DD') : undefined

    const queryClient = useQueryClient()

    const slotsQuery = useQuery({
        queryKey: ['slots', token, dateParam],
        queryFn: () => Api<SlotRow[]>('/webui/slot/get_all', 'POST', dateParam ? { date: dateParam } : {}, { token }),
        staleTime: 30 * 1000,
    })

    // Slots only carry a storeId; look the display name up from the same pickup list the
    // Add Slot dialog uses.
    const getPickupList = useQuery({
        queryKey: ['pickupList', token],
        queryFn: () => Api<OutletDetails[]>('/webui/pickup/list', 'POST', {}, { token }),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    })
    const storeNameById = useMemo(
        () => new Map((getPickupList.data ?? []).map(store => [store.storeId, store.address?.name])),
        [getPickupList.data],
    )
    const storeName = (storeId: string) => storeNameById.get(storeId) || storeId

    // Both invalidate (so any other cached dateParam variant of this query - e.g. from switching
    // the date filter earlier in the session - doesn't keep serving stale data next time it's
    // used) and explicitly refetch the one currently on screen, so a slot just created/edited/
    // deleted via a dialog is guaranteed to show up the moment it closes rather than waiting on
    // the next staleTime-driven background refetch.
    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ['slots'] })
        return slotsQuery.refetch()
    }

    // Memoised so the fallback [] keeps a stable identity and the filter memos below
    // aren't invalidated on every render while the query is still loading.
    const rows: SlotRow[] = useMemo(() => slotsQuery.data ?? [], [slotsQuery.data])

    const [statusFilter, setStatusFilter] = useState(ANY)
    const [storeFilter, setStoreFilter] = useState(ANY)
    const [slotIdQuery, setSlotIdQuery] = useState('')
    const [groupBy, setGroupBy] = useState<'store' | 'startTime' | 'none'>('store')
    // Collapsed by default, same as the gig grouping on the Orders page.
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
    // Held as a snapshot rather than read back out of the query, so a background refetch
    // can't swap the row out from under an open copy dialog.
    const [copySource, setCopySource] = useState<SlotRow | null>(null)
    const [isCopyOpen, setIsCopyOpen] = useState(false)
    const [editSource, setEditSource] = useState<SlotRow | null>(null)
    const [isEditOpen, setIsEditOpen] = useState(false)
    const [deleteSource, setDeleteSource] = useState<SlotRow | null>(null)
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)

    // Only offer values that actually occur, so a filter can never yield an empty table.
    const statusOptions = useMemo(
        () => [...new Set(rows.map(row => row.status).filter(Boolean))].sort(),
        [rows],
    )
    const storeOptions = useMemo(
        () => [...new Set(rows.map(row => row.storeId).filter(Boolean))]
            .sort((a, b) => storeName(a).localeCompare(storeName(b), undefined, { numeric: true }))
            .map(storeId => ({ label: storeName(storeId), value: storeId })),
        [rows, storeNameById],
    )

    // /slot/get_all now scopes by dateParam server-side, so this date check is normally a
    // no-op — kept anyway as a cheap backstop (filtering an already date-scoped array costs
    // nothing) in case the server-side filtering is ever incomplete.
    const slotIdPrefix = slotIdQuery.trim().toLowerCase()
    const filteredRows = useMemo(
        () => rows.filter(row =>
            (statusFilter === ANY || row.status === statusFilter)
            && (storeFilter === ANY || row.storeId === storeFilter)
            && (!dateFilter || dayjs(row.pickupSlotStart).isSame(dateFilter, 'day'))
            && (slotIdPrefix === '' || (row.slotId || '').toLowerCase().startsWith(slotIdPrefix))),
        [rows, statusFilter, storeFilter, dateFilter, slotIdPrefix],
    )

    // Groups the (already filtered) rows by store name or by pickup start time-of-day, sorted
    // by that group key so the table reads consistently regardless of the rows' original order.
    // Empty when groupBy is 'none' — that case renders filteredRows flat instead.
    const groupedRows = useMemo(() => {
        if (groupBy === 'none') return []
        const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
        const groupKey = (row: SlotRow) => groupBy === 'store'
            ? storeName(row.storeId)
            : fmtTime(new Date(row.pickupSlotStart))

        const groups = new Map<string, SlotRow[]>()
        filteredRows.forEach(row => {
            const key = groupKey(row)
            const existing = groups.get(key)
            if (existing) existing.push(row)
            else groups.set(key, [row])
        })
        return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    }, [filteredRows, groupBy, storeNameById])

    const toggleGroup = (key: string) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }))

    const isFiltered = statusFilter !== ANY || storeFilter !== ANY || !!dateFilter || slotIdPrefix !== ''
    const clearFilters = () => {
        setStatusFilter(ANY)
        setStoreFilter(ANY)
        setDateFilter(undefined)
        setSlotIdQuery('')
    }

    const startCopy = (row: SlotRow) => {
        setCopySource(row)
        setIsCopyOpen(true)
    }

    const startEdit = (row: SlotRow) => {
        setEditSource(row)
        setIsEditOpen(true)
    }

    const startDelete = (row: SlotRow) => {
        setDeleteSource(row)
        setIsDeleteOpen(true)
    }

    const renderRow = (row: SlotRow) => {
        const start = new Date(row.pickupSlotStart)
        const end = new Date(row.pickupSlotEnd)
        const fmt = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
        const date = start.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })
        // Once a slot is out for delivery or done, Prosquad no longer accepts edits/closes on it
        // (a closed requirement 409s with REQUIREMENT_CLOSED) - grey the actions out up front
        // rather than let the user hit that error.
        const isLocked = LOCKED_STATUSES.has(row.status)
        const lockedReason = 'This slot is no longer editable.'
        return (
            <tr key={row.slotId} className="hover:bg-gray-50">
                <td className="px-3 py-2">{row.slotId}</td>
                <td className="px-3 py-2">{storeName(row.storeId)}</td>
                <td className="px-3 py-2">{date}</td>
                <td className="px-3 py-2">{fmt(start)}</td>
                <td className="px-3 py-2">{fmt(end)}</td>
                <td className="px-3 py-2 text-right">{row.gigsCreated}/{row.numOfRidersReqd}</td>
                <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyles[row.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {row.status}
                    </span>
                </td>
                <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                        <Tooltip title={isLocked ? lockedReason : 'Edit riders required, guarantee amount and min service time'}>
                            <button
                                type="button"
                                aria-label={`Edit slot ${row.slotId}`}
                                disabled={isLocked}
                                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:pointer-events-none disabled:hover:bg-transparent cursor-pointer"
                                onClick={() => startEdit(row)}
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                        </Tooltip>
                        <Tooltip title="Create a new slot from this one">
                            <button
                                type="button"
                                aria-label={`Copy slot ${row.slotId}`}
                                className="p-1 rounded hover:bg-gray-200 cursor-pointer"
                                onClick={() => startCopy(row)}
                            >
                                <Copy className="w-4 h-4" />
                            </button>
                        </Tooltip>
                        <Tooltip title={isLocked ? lockedReason : 'Delete this slot'}>
                            <button
                                type="button"
                                aria-label={`Delete slot ${row.slotId}`}
                                disabled={isLocked}
                                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:pointer-events-none disabled:hover:bg-transparent cursor-pointer"
                                onClick={() => startDelete(row)}
                            >
                                <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                        </Tooltip>
                    </div>
                </td>
            </tr>
        )
    }

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">Slots</h1>
                <div className="flex items-center gap-2">
                    <UploadSlotsDialog onSuccess={refresh}>
                        <Button size="sm" variant="outline">
                            <Upload className="w-4 h-4 mr-1" /> Upload from File
                        </Button>
                    </UploadSlotsDialog>
                    <SlotDialog onSuccess={refresh}>
                        <Button size="sm">
                            <Plus className="w-4 h-4 mr-1" /> Add Slot
                        </Button>
                    </SlotDialog>
                </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <div className="p-1">
                    <p className="mb-1 ml-1 text-sm font-medium text-gray-500">Date</p>
                    <DatePicker date={dateFilter} onChange={setDateFilter} />
                </div>
                <div className="w-48">
                    <Select
                        label="Status"
                        options={[{ label: 'All statuses', value: ANY }, ...statusOptions.map(s => ({ label: s, value: s }))]}
                        value={statusFilter}
                        onChange={setStatusFilter}
                    />
                </div>
                <div className="w-48">
                    <Select
                        label="Store"
                        options={[{ label: 'All stores', value: ANY }, ...storeOptions]}
                        value={storeFilter}
                        onChange={setStoreFilter}
                    />
                </div>
                <div className="w-56">
                    <Input
                        label="Slot ID starts with"
                        placeholder="e.g. 20260823"
                        value={slotIdQuery}
                        onChange={e => setSlotIdQuery(e.target.value)}
                    />
                </div>
                <div className="w-48">
                    <Select
                        label="Group By"
                        options={[
                            { label: 'Store Name', value: 'store' },
                            { label: 'Slot Start Time', value: 'startTime' },
                            { label: 'None', value: 'none' },
                        ]}
                        value={groupBy}
                        onChange={val => setGroupBy(val as 'store' | 'startTime' | 'none')}
                    />
                </div>
                {isFiltered && (
                    <div className="flex items-center gap-3 p-1 pb-2">
                        <Button size="sm" variant="outline" onClick={clearFilters}>
                            <X className="w-4 h-4 mr-1" /> Clear
                        </Button>
                        <span className="text-sm text-gray-500">
                            {filteredRows.length} of {rows.length}
                        </span>
                    </div>
                )}
            </div>

            {slotsQuery.isLoading && <p className="text-sm text-gray-500">Loading…</p>}
            {slotsQuery.isError && <p className="text-sm text-red-500">Failed to load slots.</p>}

            {!slotsQuery.isLoading && rows.length === 0 && (
                <p className="text-sm text-gray-400">No slots found.</p>
            )}

            {rows.length > 0 && filteredRows.length === 0 && (
                <p className="text-sm text-gray-400">No slots match these filters.</p>
            )}

            {filteredRows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Slot ID</th>
                                <th className="px-3 py-2 text-left">Store</th>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Pickup Start</th>
                                <th className="px-3 py-2 text-left">Pickup End</th>
                                <th className="px-3 py-2 text-right">Rider Acceptance</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {groupBy === 'none' ? (
                                filteredRows.map(row => renderRow(row))
                            ) : (
                                groupedRows.map(([groupLabel, groupRows]) => {
                                    const isExpanded = expandedGroups[groupLabel] ?? false
                                    return (
                                        <Fragment key={groupLabel}>
                                            <tr
                                                className="bg-slate-100 hover:bg-slate-200 cursor-pointer"
                                                onClick={() => toggleGroup(groupLabel)}
                                            >
                                                <td colSpan={8} className="px-3 py-2">
                                                    <div className="w-full flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                            <p className="font-semibold text-slate-800 text-sm">
                                                                {groupBy === 'store' ? groupLabel : `Start Time: ${groupLabel}`}
                                                            </p>
                                                        </div>
                                                        <p className="text-slate-700 text-sm">
                                                            {groupRows.length} slot{groupRows.length > 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded ? groupRows.map(row => renderRow(row)) : null}
                                        </Fragment>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <SlotDialog
                template={copySource}
                open={isCopyOpen}
                onOpenChange={setIsCopyOpen}
                onSuccess={refresh}
            />

            <EditSlotDialog
                slot={editSource}
                open={isEditOpen}
                onOpenChange={setIsEditOpen}
                onSuccess={refresh}
            />

            <DeleteSlotDialog
                slot={deleteSource}
                open={isDeleteOpen}
                onOpenChange={setIsDeleteOpen}
                onSuccess={refresh}
            />
        </div>
    )
}
